'use client';

import type { ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { categoryModels, generatorCategories, wanModelLabel, type GeneratorCategoryId } from '@/lib/wan/categories';
import { wanMinimumCredits } from '@/lib/wan/catalog';
import { versionCategories } from '@/lib/wan/pages';

export type RenderGeneratorSelectors = (disabled: boolean) => ReactNode;
type SelectorOption = { value: string; label: string; minimumCredits?: number };

export function GeneratorSelectors({ categoryId, modelKey, version, disabled, onCategoryChange, onModelChange }: {
  categoryId: GeneratorCategoryId;
  modelKey: string;
  version?: string;
  disabled: boolean;
  onCategoryChange: (id: GeneratorCategoryId) => void;
  onModelChange: (key: string) => void;
}) {
  const zh = useLocale().startsWith('zh');
  const t = useTranslations('wan22');
  const category = generatorCategories.find(item => item.id === categoryId)!;
  const categoryLabel = (item: typeof category) => item.legacy ? t(`modes.${item.id.replaceAll('-', '_')}`) : item.label[zh ? 0 : 1];
  const fields: { id: string; label: string; value: string; onChange: (value: string) => void; options: SelectorOption[] }[] = [
    { id: 'generation-type', label: zh ? '切换生成模式' : 'Generation mode', value: categoryId,
      onChange: onCategoryChange as (value: string) => void,
      options: (version ? versionCategories(version) : generatorCategories).map(item => ({ value: item.id, label: categoryLabel(item) })) },
    { id: 'generation-model', label: zh ? '选择模型' : 'Model', value: modelKey, onChange: onModelChange,
      options: categoryModels(categoryId).map(model => ({ value: model.key, label: wanModelLabel(model), minimumCredits: wanMinimumCredits(model) })) },
  ];
  return <div className="flex flex-col gap-6">
    {fields.filter(field => field.options.length > 1).map(field => <div key={field.id} className="flex min-w-0 flex-col gap-2">
      <Label htmlFor={field.id} className="text-sm font-medium text-white">{field.label}</Label>
      <Select value={field.value} onValueChange={value => {
        // Radix's hidden form select can emit an empty value as its options change.
        // Only navigate for a new, available selection made while the control is enabled.
        if (!disabled && value !== field.value && field.options.some(option => option.value === value)) {
          field.onChange(value);
        }
      }} disabled={disabled}>
        <SelectTrigger id={field.id} className="w-full gap-2 border-green-500/20 bg-gray-800/60 text-white backdrop-blur-sm focus:border-green-400/60 focus:ring-green-400/15"><SelectValue className="flex-1 text-left" /></SelectTrigger>
        <SelectContent className="border-green-500/20 bg-gray-800/95 backdrop-blur-xl"><SelectGroup>
          {field.options.map(option => <SelectItem key={option.value} value={option.value} textValue={option.label} className="text-white focus:bg-green-500/15 focus:text-green-400 [&>span:last-child]:w-full">
            <span className="flex min-w-0 items-center justify-between gap-3">
              <span className="truncate">{option.label}</span>{' '}
              {option.minimumCredits !== undefined && <span className="shrink-0 text-xs tabular-nums text-emerald-300">{zh ? `${option.minimumCredits} 积分起` : `from ${option.minimumCredits} credits`}</span>}
            </span>
          </SelectItem>)}
        </SelectGroup></SelectContent>
      </Select>
    </div>)}
  </div>;
}
